import { motion, AnimatePresence } from 'framer-motion';

interface OutOfGearModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function OutOfGearModal({ isOpen, onClose }: OutOfGearModalProps) {
  const isNativeApp = (() => {
    if (typeof window === 'undefined') return false;
    const cap = (window as any).Capacitor;
    if (!cap) return false;
    try {
      const plat = cap.getPlatform?.() || cap.platform;
      return plat === 'ios' || plat === 'android';
    } catch {
      return false;
    }
  })();

  const nativeLayout = isNativeApp;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-0 z-50 flex justify-center p-4 ${nativeLayout ? 'items-center' : 'items-center md:items-start md:pt-24'}`}
          style={{ background: 'rgba(0, 0, 0, 0.7)' }}
          onClick={(e) => {
            // Close when clicking backdrop
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }
          }}
        >
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`relative rounded-2xl border-4 ${nativeLayout ? 'w-[75vw]' : 'w-full max-w-md'}`}
            style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              padding: nativeLayout ? '0.875rem' : '1.5rem',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-2xl font-bold leading-none text-gray-600 hover:text-white hover:bg-gray-800 transition-colors cursor-pointer"
              style={{ zIndex: 50, pointerEvents: 'auto' }}
              aria-label="Close"
            >
              ✕
            </button>

            {/* Header */}
            <div className="mb-3">
              <h2 className={`font-black ${nativeLayout ? 'text-xl mb-1' : 'text-2xl md:text-3xl mb-2'}`} style={{ color: '#3e2615' }}>
                You&apos;re Out of Gear!
              </h2>
              <p className={`${nativeLayout ? 'text-base' : 'text-lg'}`} style={{ color: '#3e2615' }}>
                Your pan&apos;s run dry, prospector.
              </p>
            </div>

            {/* Two-column content */}
            <div className={`grid ${nativeLayout ? 'grid-cols-2 gap-3 mb-4' : 'grid-cols-1 gap-3 mb-4'}`}>
              <div>
                <div className={`font-semibold mb-2 ${nativeLayout ? 'text-sm' : 'text-base'}`} style={{ color: '#3e2615' }}>
                  🎮 Register now to:
                </div>
                <ul className={`ml-4 list-inside list-disc space-y-1 ${nativeLayout ? 'text-xs' : 'text-sm'}`} style={{ color: '#3e2615' }}>
                  <li>Refill and buy Gear anytime</li>
                  <li>Play against real humans and clever AIs</li>
                  <li>Climb the leaderboards!</li>
                </ul>
              </div>

              <div className="flex flex-col justify-center">
                <p className={`${nativeLayout ? 'text-xs' : 'text-sm'} italic`} style={{ color: '#3e2615' }}>
                  To keep digging for gold, you&apos;ll need more Gear - and that means creating an account.
                </p>
                <p className={`${nativeLayout ? 'text-xs mt-2' : 'text-sm mt-3'} italic font-semibold`} style={{ color: '#3e2615' }}>
                  Don&apos;t lose your progress - claim your name and keep striking gold!
                </p>
              </div>
            </div>

            {/* Button at bottom center */}
            <div className="flex flex-col items-center mt-auto">
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('showLoginModal'));
                  onClose();
                }}
                className={`rounded-lg font-bold transition-all hover:scale-105 ${nativeLayout ? 'px-5 py-2.5 text-base' : 'px-6 py-3 text-lg'}`}
                style={{
                  background: '#fffaeb',
                  color: '#3e2615',
                  border: '2px solid #8b6542',
                  boxShadow: '0 4px 0 #8b6542',
                  textAlign: 'center'
                }}
              >
                Register &amp; Refill Gear
              </button>
              <p className={`${nativeLayout ? 'text-[10px] mt-1' : 'text-xs mt-2'}`} style={{ color: '#3e2615', opacity: 0.8 }}>
                Or check back tomorrow for more gear.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
